/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import _ from 'lodash';
import BasicIcon from '../BasicIcon/BasicIcon';
import { ArrowContainer } from '../DatePicker/styles';
import Input from '../Input/Input';
import { InputContainer } from '../Input/styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { ID } from '../utils/arrays';
import DropdownOverlay from './MultiSelectOverlay/MultiSelectOverlay';
import { MultiSelectInputProps } from './types';

/**
 *
 * @param placeholder
 * @param labeled
 * @param options [{id, name}, ...] by default
 * @param keyProperty Name of the property asociated to 'id'. Default: 'id'
 * @param showProperty Name of the property asociated to label to show on each option. Default: 'name'
 * @param onChange
 * @param dropDownOnly If 'true' the behavior is like Select component
 * @param readOnly If 'true' the values cant be modified
 * @param disabled Its a boolean
 *
 */
const MultiSelectInput: React.FC<MultiSelectInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    options = [],
    label,
    disabled,
    keyProperty = 'id',
    showProperty = 'name',
    readOnly,
    testId = 'select',
    size,
    variant = 'underline',
    labelAll = 'Todos',
    isValueObject,
    overlayPosition,
    emptyStateLabel,
    overAll = false,
    isLoading,
    onChange,
    onFocus,
    onBlur,
    onMouseDown,
    inputRef,
    helperText,
  } = props;
  const chevronRef = useRef<HTMLDivElement>(null);
  const defaultInputRef = useRef<HTMLDivElement>(null);
  const localInputRef = inputRef || defaultInputRef;
  const { actualTheme, isMobile } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);

  const [optionsState, setOptionsState] = useState([]);

  const [generatedId] = useState(ID());
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputSize = useMemo(() => {
    if (!size && isMobile) return 'small';
    return size || 'standard';
  }, [size, isMobile]);

  const inputHeight = useMemo(() => {
    const s = inputSize || 'standard';
    const h = actualTheme.input.sizes[s].height[variant];
    return h;
  }, [actualTheme, inputSize, variant]);

  const handleClick = useCallback(() => {
    if (!disabled && !readOnly) {
      setIsOpen((prev) => !prev);
      if (onMouseDown) onMouseDown();
    }
  }, [disabled, onMouseDown, readOnly]);

  const reduceValue = useCallback(
    () =>
      optionsState
        .filter((ob) => ob.selected)
        .map((ob) => _.get(ob, showProperty))
        .join(', '),
    [optionsState, showProperty],
  );

  const onChangeHandlerAll = useCallback(
    (e: any) => {
      e.stopPropagation();

      const all = optionsState.every((ob) => ob.selected);
      const map = optionsState.map((ob) => ({ ...ob, selected: !all }));
      setOptionsState(map);

      const values = isValueObject
        ? map
            .filter((ob) => ob.selected)
            .map((ob) => {
              const obValue = { ...ob };
              delete obValue.selected;
              return obValue;
            })
        : map
            .filter((ob) => ob.selected)
            .map((ob) => _.get(ob, keyProperty))
            .join(',');

      if (input) {
        input.onChange(values);
      }

      if (onChange) {
        onChange(values);
      }
    },
    [input, isValueObject, keyProperty, onChange, optionsState],
  );

  const handleChange = useCallback(
    (valueChange: any) => (e: any) => {
      e.stopPropagation();

      const opt = [...optionsState];
      const index = opt.findIndex(
        (ob) => _.get(ob, keyProperty) === _.get(valueChange, keyProperty),
      );

      opt[index].selected = !valueChange.selected;
      setOptionsState(opt);

      const values = isValueObject
        ? opt
            .filter((ob) => ob.selected)
            .map((ob) => {
              const obValue = { ...ob };
              delete obValue.selected;
              return obValue;
            })
        : opt
            .filter((ob) => ob.selected)
            .map((ob) => _.get(ob, keyProperty))
            .join(',');

      if (input) {
        input.onChange(values);
      }

      if (onChange) {
        onChange(values);
      }
    },
    [input, isValueObject, keyProperty, onChange, optionsState],
  );

  useEffect(() => {
    if (!input.value && input.value !== 0) {
      setOptionsState(
        options.map((ob) => ({
          ...ob,
          selected:
            isValueObject && Array.isArray(input.value)
              ? input.value.some(
                  (value: any) => _.get(value, keyProperty) === _.get(ob, keyProperty),
                )
              : input.value.split(',').includes(_.get(ob, keyProperty).toString()),
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, keyProperty]);

  useEffect(() => {
    setOptionsState(
      options.map((ob) => ({
        ...ob,
        selected:
          isValueObject && Array.isArray(input.value)
            ? input.value.some((value: any) => _.get(value, keyProperty) === _.get(ob, keyProperty))
            : input.value.split(',').includes(_.get(ob, keyProperty).toString()),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, keyProperty]);

  const renderChevronIcon = useCallback(
    () => (
      <>
        <ArrowContainer
          onClick={handleClick}
          isOpen={isOpen}
          disabled={readOnly || disabled}
          data-testid={`${testId}-chevron`}
          ref={chevronRef}
        >
          <BasicIcon
            size="small"
            name={isLoading ? 'spinner' : 'chevron-down'}
            color={
              hasError
                ? actualTheme.status.error.background
                : disabled
                ? actualTheme.grayB8
                : actualTheme.gray80
            }
            clickable
          />
        </ArrowContainer>
      </>
    ),
    [
      handleClick,
      isOpen,
      readOnly,
      disabled,
      testId,
      isLoading,
      hasError,
      actualTheme.status.error.background,
      actualTheme.grayB8,
      actualTheme.gray80,
    ],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <InputContainer id="select-id">
        <Input
          id={generatedId}
          typingAllowed={false}
          disabled={disabled}
          readOnly={readOnly}
          value={reduceValue()}
          placeholder={placeholder}
          input={input}
          meta={meta}
          label={label}
          autoComplete="off"
          pointer
          testId={testId}
          size={size}
          variant={variant}
          rightAddOn={renderChevronIcon()}
          showClearIcon={false}
          onFocus={onFocus}
          isOpen={isOpen}
          onBlur={onBlur}
          onMouseDown={handleClick}
          inputRef={localInputRef}
          ellipsis
          helperText={helperText}
        />
        <DropdownOverlay
          isOpen={isOpen && !isLoading}
          overlayPosition={overlayPosition}
          target={generatedId}
          keyProperty={keyProperty}
          showProperty={showProperty}
          options={optionsState}
          setIsOpen={setIsOpen}
          onSelection={handleChange}
          onSelectAll={onChangeHandlerAll}
          inputValue={input?.value}
          targetHeight={inputHeight}
          labelAll={labelAll}
          chevronRef={chevronRef}
          emptyStateLabel={emptyStateLabel}
          overAll={overAll}
          testId={testId}
          inputRef={localInputRef}
        />
      </InputContainer>
    </ThemeProvider>
  );
};

export default MultiSelectInput;
